import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Rocket } from "@/components/Rocket";
import { useLang } from "@/i18n/LanguageContext";
import { LOCATIONS } from "@/data/site";

export const NAV_LINKS = [
  { to: "/", key: "home", id: "home" },
  { to: "/programs", key: "programs", id: "programs" },
  { to: "/about", key: "philosophy", id: "philosophy" },
  { to: "/locations", key: "locations", id: "locations" },
  { to: "/gallery", key: "gallery", id: "gallery" },
];

export const LangToggle = ({ className = "", testId = "lang-toggle" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-full border border-ink/15 p-0.5 font-mono text-[11px] font-semibold ${className}`} role="group" aria-label="Language" data-testid={testId}>
      {["en", "es"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`${testId}-${l}`}
          aria-pressed={lang === l}
          className={`rounded-full px-2.5 py-1 uppercase tracking-wider transition-colors duration-200 ${
            lang === l ? "bg-ink text-cream" : "text-ink/60 hover:text-ink"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const DesktopNav = () => {
  const { t } = useLang();
  return (
    <>
      <nav className="mx-3 hidden items-center gap-4 lg:flex xl:mx-0 xl:gap-7" data-testid="nav-desktop">
        {NAV_LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            data-testid={`nav-link-${l.id}`}
            className={({ isActive }) =>
              `text-sm font-semibold transition-colors duration-200 hover:text-brand ${isActive ? "text-brand" : "text-ink/70"}`
            }
          >
            {t(`nav.${l.key}`)}
          </NavLink>
        ))}
      </nav>
      <div className="hidden items-center gap-2 lg:flex xl:gap-3">
        <LangToggle />
        <div className="flex flex-col rounded-2xl border border-ink/15 px-3 py-1 leading-tight" data-testid="nav-call-group">
          {LOCATIONS.map((loc) => (
            <a key={loc.id} href={loc.phoneHref} data-testid={`nav-call-btn-${loc.id}`} title={loc.name}
              className="flex items-center gap-1.5 whitespace-nowrap py-0.5 text-xs font-semibold text-ink transition-colors duration-200 hover:text-brand">
              <Phone className="h-3 w-3 shrink-0" />
              <span className="hidden w-12 font-mono text-[9px] uppercase tracking-wider text-mist xl:inline">{loc.short}</span>
              {loc.phone}
            </a>
          ))}
        </div>
        <Link to="/book" data-testid="nav-book-tour-btn"
          className="whitespace-nowrap rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-brandsky hover:shadow-lg">
          {t("nav.book")}
        </Link>
      </div>
    </>
  );
};

const MobileMenu = ({ onClose }) => {
  const { t } = useLang();
  const { pathname } = useLocation();
  return (
    <div className="mt-2 rounded-2xl border border-ink/10 bg-cream/95 p-4 shadow-lg backdrop-blur-xl lg:hidden" data-testid="nav-mobile-panel">
      {NAV_LINKS.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          onClick={onClose}
          data-testid={`nav-mobile-link-${l.id}`}
          className={`block rounded-xl px-4 py-3 text-base font-semibold ${pathname === l.to ? "bg-sand text-brand" : "text-ink/80"}`}
        >
          {t(`nav.${l.key}`)}
        </NavLink>
      ))}
      <Link to="/book" onClick={onClose} data-testid="nav-mobile-book-tour-btn"
        className="mt-2 block rounded-xl bg-brand px-4 py-3 text-center text-base font-semibold text-white">
        {t("nav.book")}
      </Link>
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mt-4 flex items-center justify-between rounded-2xl border border-ink/10 bg-cream/85 px-4 py-3 shadow-sm backdrop-blur-xl sm:px-6">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
            <Rocket className="h-9 w-9" />
            <span className="whitespace-nowrap font-display text-lg font-semibold leading-none tracking-tight">
              Learning <span className="text-brand">&amp;</span> Beyond
              <span className="block font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-mist">{t("nav.tagline")}</span>
            </span>
          </Link>

          <DesktopNav />

          <div className="flex items-center gap-2 lg:hidden">
            <LangToggle testId="lang-toggle-mobile" />
            <button className="rounded-lg p-2 text-ink" onClick={() => setOpen(!open)} data-testid="nav-mobile-menu-btn" aria-label="Toggle menu">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {open && <MobileMenu onClose={() => setOpen(false)} />}
      </div>
    </header>
  );
};
